import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Tabs, Tab, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { colors } from '../../brand-colors';

const ASSET_TYPE_LABELS = {
  social_post: 'Social Posts',
  long_form: 'Blog Posts',
  short_video: 'Video Scripts',
  repost: 'Reposts',
  curated: 'Curated',
};

const CATEGORY_LABELS = {
  blog_clips: 'Blog + Clips',
  pure_engagement: 'Engagement',
  engagement: 'Engagement',
  lead_magnet: 'Lead Magnet',
  book_excerpt: 'Book Excerpt',
  book_excerpts: 'Book Excerpt',
  thought_leader: 'Thought Leader',
  contextual_proof: 'Contextual Proof',
  direct_offer: 'Direct Offer',
  blog_promo: 'Blog Promo',
};

const STATUS_TABS = [
  { key: 'pending_approval', label: 'Pending Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'published', label: 'Published' },
  { key: 'archived', label: 'Archived' },
  { key: 'rejected', label: 'Rejected' },
];

function ApprovalQueue() {
  const [assets, setAssets] = useState([]);
  const [typeCounts, setTypeCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStatus, setActiveStatus] = useState('pending_approval');
  const [activeType, setActiveType] = useState(null); // null = all types
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  // Edit modal
  const [editAsset, setEditAsset] = useState(null);
  const [editText, setEditText] = useState('');
  const [editTitle, setEditTitle] = useState('');

  // Bulk action feedback
  const [actionMessage, setActionMessage] = useState(null);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `/api/weekly-assets/?status=${activeStatus}&page=${page}&per_page=20`;
      if (activeType) url += `&asset_type=${activeType}`;

      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to load assets');

      const data = await response.json();
      setAssets(data.assets || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setTypeCounts(data.type_counts || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeStatus, activeType, page]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [activeStatus, activeType]);

  const handleAction = async (assetId, action, body = {}) => {
    try {
      const method = action === 'regenerate' ? 'POST' : 'PUT';
      const response = await fetch(`/api/weekly-assets/${assetId}/${action}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Action failed');
      }
      fetchAssets();
    } catch (err) {
      setActionMessage({ type: 'danger', text: err.message });
      setTimeout(() => setActionMessage(null), 4000);
    }
  };

  const handleBulkApprove = async (mode, minScore = 4.5) => {
    try {
      const response = await fetch('/api/weekly-assets/bulk-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode, min_score: minScore }),
      });
      if (!response.ok) throw new Error('Bulk approve failed');
      const data = await response.json();
      setActionMessage({ type: 'success', text: `Approved ${data.approved_count} assets` });
      setTimeout(() => setActionMessage(null), 4000);
      fetchAssets();
    } catch (err) {
      setActionMessage({ type: 'danger', text: err.message });
      setTimeout(() => setActionMessage(null), 4000);
    }
  };

  const handleEditSave = async (approve = false) => {
    if (!editAsset) return;
    try {
      const response = await fetch(`/api/weekly-assets/${editAsset.id}/edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          content: { text: editText, title: editTitle },
          approve,
        }),
      });
      if (!response.ok) throw new Error('Save failed');
      setEditAsset(null);
      fetchAssets();
    } catch (err) {
      setActionMessage({ type: 'danger', text: err.message });
      setTimeout(() => setActionMessage(null), 4000);
    }
  };

  const openEdit = (asset) => {
    setEditAsset(asset);
    setEditText(asset.content?.text || asset.content?.body || '');
    setEditTitle(asset.content?.title || '');
  };

  const pendingCount = typeCounts ? Object.values(typeCounts).reduce((a, b) => a + b, 0) : 0;

  return (
    <Container fluid className="py-4" style={{ maxWidth: 1100 }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 style={{
            fontFamily: "'Trajan Pro', 'Times New Roman', serif",
            letterSpacing: '0.15em',
            color: colors.greyDarkest,
            marginBottom: 4,
          }}>
            APPROVAL QUEUE
          </h3>
          <p className="text-muted mb-0" style={{ fontSize: 14 }}>
            Review and approve your weekly marketing assets
          </p>
        </div>
        {activeStatus === 'pending_approval' && total > 0 && (
          <div className="d-flex gap-2">
            <Button
              variant="outline-dark"
              size="sm"
              onClick={() => handleBulkApprove('threshold', 4.5)}
            >
              Approve All 4.5+
            </Button>
            <Button
              size="sm"
              style={{
                background: `linear-gradient(90deg, ${colors.goldLight} 0%, ${colors.goldDark} 100%)`,
                border: 'none',
                color: '#fff',
              }}
              onClick={() => handleBulkApprove('all')}
            >
              Approve All ({total})
            </Button>
          </div>
        )}
      </div>

      {actionMessage && (
        <Alert variant={actionMessage.type} dismissible onClose={() => setActionMessage(null)}>
          {actionMessage.text}
        </Alert>
      )}

      {/* Status tabs */}
      <Tabs
        activeKey={activeStatus}
        onSelect={(k) => setActiveStatus(k)}
        className="mb-3"
        style={{ borderBottom: `2px solid ${colors.greyLight}` }}
      >
        {STATUS_TABS.map(tab => (
          <Tab
            key={tab.key}
            eventKey={tab.key}
            title={
              <span style={{
                color: activeStatus === tab.key ? colors.goldLight : colors.greyMid,
                fontWeight: activeStatus === tab.key ? 600 : 400,
              }}>
                {tab.label}
                {tab.key === 'pending_approval' && pendingCount > 0 && (
                  <Badge
                    bg="warning"
                    text="dark"
                    className="ms-2"
                    style={{ fontSize: 10 }}
                  >
                    {pendingCount}
                  </Badge>
                )}
              </span>
            }
          />
        ))}
      </Tabs>

      {/* Type filter pills */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Button
          size="sm"
          variant={!activeType ? 'dark' : 'outline-secondary'}
          onClick={() => setActiveType(null)}
          style={!activeType ? { background: colors.greyDarkest, color: colors.goldLight } : {}}
        >
          All Types
        </Button>
        {Object.entries(ASSET_TYPE_LABELS).map(([key, label]) => {
          const count = typeCounts[key] || 0;
          if (count === 0 && activeStatus === 'pending_approval') return null;
          return (
            <Button
              key={key}
              size="sm"
              variant={activeType === key ? 'dark' : 'outline-secondary'}
              onClick={() => setActiveType(key)}
              style={activeType === key ? { background: colors.greyDarkest, color: colors.goldLight } : {}}
            >
              {label} {count > 0 && <Badge bg="secondary" className="ms-1">{count}</Badge>}
            </Button>
          );
        })}
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: colors.goldMid }} />
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Empty state */}
      {!loading && !error && assets.length === 0 && (
        <Card className="text-center p-5" style={{ border: `1px solid ${colors.greyLight}` }}>
          <p className="text-muted mb-0">
            {activeStatus === 'pending_approval'
              ? 'No assets pending review. Your next batch will be generated on your weekly anniversary date.'
              : `No ${activeStatus.replace('_', ' ')} assets.`}
          </p>
        </Card>
      )}

      {/* Asset cards */}
      {!loading && assets.map(asset => (
        <AssetCard
          key={asset.id}
          asset={asset}
          onApprove={() => handleAction(asset.id, 'approve')}
          onReject={() => handleAction(asset.id, 'reject')}
          onEdit={() => openEdit(asset)}
          onRegenerate={() => handleAction(asset.id, 'regenerate')}
          showActions={activeStatus === 'pending_approval' || activeStatus === 'archived'}
        />
      ))}

      {/* Pagination */}
      {pages > 1 && (
        <div className="d-flex justify-content-center gap-2 mt-4">
          <Button
            variant="outline-dark"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="align-self-center text-muted" style={{ fontSize: 13 }}>
            Page {page} of {pages}
          </span>
          <Button
            variant="outline-dark"
            size="sm"
            disabled={page >= pages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Edit Modal */}
      <Modal show={!!editAsset} onHide={() => setEditAsset(null)} size="lg">
        <Modal.Header
          closeButton
          style={{ background: colors.greyDarkest, borderBottom: 'none' }}
        >
          <Modal.Title style={{
            color: colors.goldLight,
            fontFamily: "'Trajan Pro', 'Times New Roman', serif",
            letterSpacing: '0.1em',
            fontSize: 16,
          }}>
            EDIT ASSET
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editAsset && (
            <>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: 13 }}>Title</Form.Label>
                <Form.Control
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: 13 }}>Content</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                />
              </Form.Group>
              {editAsset.image_path && (
                <div className="mb-3">
                  <Form.Label style={{ fontWeight: 600, fontSize: 13 }}>Image</Form.Label>
                  <div>
                    <img
                      src={editAsset.image_path}
                      alt="Asset"
                      style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8 }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setEditAsset(null)}>
            Cancel
          </Button>
          <Button variant="outline-dark" onClick={() => handleEditSave(false)}>
            Save Draft
          </Button>
          <Button
            onClick={() => handleEditSave(true)}
            style={{
              background: `linear-gradient(90deg, ${colors.goldLight} 0%, ${colors.goldDark} 100%)`,
              border: 'none',
              color: '#fff',
            }}
          >
            Save & Approve
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}


function AssetCard({ asset, onApprove, onReject, onEdit, onRegenerate, showActions }) {
  const [expanded, setExpanded] = useState(false);
  const content = asset.content || {};
  const text = content.text || content.body || '';
  const title = content.title || '';
  const isLong = text.length > 300;

  const scoreColor = (score) => {
    if (!score) return colors.greyMid;
    if (score >= 4.5) return '#28a745';
    if (score >= 4.0) return colors.goldDark;
    return '#dc3545';
  };

  return (
    <Card className="mb-3" style={{ border: `1px solid ${colors.greyLight}` }}>
      <Card.Body className="p-3">
        <Row>
          {/* Image column */}
          {asset.image_path && (
            <Col xs={3} md={2}>
              <img
                src={asset.image_path}
                alt=""
                style={{
                  width: '100%',
                  borderRadius: 6,
                  objectFit: 'cover',
                  aspectRatio: '16/9',
                }}
              />
            </Col>
          )}

          {/* Content column */}
          <Col>
            {/* Header row */}
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <Badge
                  bg="dark"
                  style={{ fontSize: 10, background: colors.greyDarkest }}
                >
                  {ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}
                </Badge>
                {asset.content_mix_category && (
                  <Badge
                    bg="secondary"
                    style={{ fontSize: 10, background: colors.goldDark }}
                  >
                    {CATEGORY_LABELS[asset.content_mix_category] || asset.content_mix_category}
                  </Badge>
                )}
                {asset.target_platforms?.map(p => (
                  <Badge key={p} bg="light" text="dark" style={{ fontSize: 10, border: `1px solid ${colors.greyLight}` }}>
                    {p}
                  </Badge>
                ))}
                {asset.variant_label && (
                  <Badge bg="info" style={{ fontSize: 10 }}>
                    Variant {asset.variant_label}
                  </Badge>
                )}
              </div>
              {asset.topic_score && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontWeight: 700,
                  fontSize: 14,
                  color: scoreColor(asset.topic_score),
                }}>
                  {asset.topic_score.toFixed(1)}
                </div>
              )}
            </div>

            {/* Title */}
            {title && (
              <h6 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, color: colors.greyDarkest }}>
                {title}
              </h6>
            )}

            {/* Content preview */}
            <div
              style={{
                fontSize: 13,
                color: '#444',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
              }}
            >
              {expanded || !isLong ? text : text.slice(0, 300) + '...'}
              {isLong && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: colors.goldDark,
                    fontSize: 12,
                    cursor: 'pointer',
                    fontWeight: 600,
                    marginLeft: 4,
                  }}
                >
                  {expanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>

            {/* Meta row */}
            <div className="d-flex justify-content-between align-items-center mt-2">
              <div style={{ fontSize: 11, color: colors.greyMid }}>
                {asset.created_at && new Date(asset.created_at).toLocaleDateString()}
                {asset.credits_expire_at && asset.status === 'pending_approval' && (
                  <span className="ms-2">
                    Expires {new Date(asset.credits_expire_at).toLocaleDateString()}
                  </span>
                )}
              </div>

              {/* Actions */}
              {showActions && (
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    style={{ fontSize: 12, padding: '2px 10px' }}
                    onClick={onRegenerate}
                  >
                    Regenerate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-dark"
                    style={{ fontSize: 12, padding: '2px 10px' }}
                    onClick={onEdit}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    style={{ fontSize: 12, padding: '2px 10px' }}
                    onClick={onReject}
                  >
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    style={{
                      fontSize: 12,
                      padding: '2px 14px',
                      background: `linear-gradient(90deg, ${colors.goldLight} 0%, ${colors.goldDark} 100%)`,
                      border: 'none',
                      color: '#fff',
                    }}
                    onClick={onApprove}
                  >
                    Approve
                  </Button>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

export default ApprovalQueue;
